/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { ChangeEvent, FC, useCallback } from "react";

import { IFieldValidationError } from "./FieldTypes";
import { FieldWrapper } from "./FieldWrapper";
import { Icon, ICON_SIZE } from "../Icon";
import { BEM } from "@adapptio/lib-components";
import { isFunction, isNil, toNumber } from "@adapptio/lib-shared";
import { Input } from "../Input";
import { InputAdornment } from "../InputAdornment";
import { InputButton } from "../InputButton";
import { FieldValidationErrors } from "./FieldValidationErrors";

export interface IFieldNumberProps {
	id?: string;
	label?: string;
	value: number;
	placeholder?: string;
	readOnly?: boolean;
	min?: number;
	max?: number;
	step?: string;
	validationErrors?: IFieldValidationError[];
	hideButtons?: boolean;
	className?: string;
	onChange?: (value: number, ev: ChangeEvent<HTMLInputElement>) => void;
	onBlur?: React.FocusEventHandler<HTMLInputElement>;
	grow?: boolean;
	inputRef?: React.MutableRefObject<HTMLInputElement>;
	suffix?: string;
	debounceTimeout?: number;
}

export const FieldNumber: FC<IFieldNumberProps> = ({
	id,
	value,
	min,
	max,
	step,
	placeholder,
	readOnly,
	validationErrors,
	className,
	grow,
	onChange,
	onBlur,
	hideButtons,
	inputRef,
	suffix,
	debounceTimeout
}) => {
	const baseClass = BEM("edt-field-control");

	const handleChange = useCallback(
		(ev: ChangeEvent<HTMLInputElement>) => {
			if (onChange) {
				const stringValue = ev.target.value;
				const parsedValue = toNumber(stringValue);

				onChange(isFinite(parsedValue) ? parsedValue : null, ev);
			}
		},
		[ onChange ]
	);

	const increment = useCallback(
		(ev: React.SyntheticEvent<HTMLButtonElement>) => {
			if (isFunction(onChange)) {
				const newValue = (value || 0) + 1;

				onChange(Math.min(newValue, isNil(max) ? Infinity : max), ev as ChangeEvent<HTMLInputElement>);
			}
		},
		[ onChange, value ]
	);

	const decrement = useCallback(
		(ev: React.SyntheticEvent<HTMLButtonElement>) => {
			if (isFunction(onChange)) {
				const newValue = (value || 0) - 1;

				onChange(Math.max(newValue, isNil(min) ? -Infinity : min), ev as ChangeEvent<HTMLInputElement>);
			}
		},
		[ onChange, value ]
	);

	return (
		<FieldWrapper type="number" className={className} grow={grow}>
			<Input
				id={id}
				type="number"
				inputMode="numeric"
				className={baseClass.toClassName()}
				value={value ?? ""}
				suffix={suffix}
				placeholder={placeholder}
				onChange={handleChange}
				onBlur={onBlur}
				min={min}
				max={max}
				step={step}
				ref={inputRef}
				readOnly={readOnly}
				debounceTimeout={debounceTimeout}
				endAdornment={
					!hideButtons && (
						<InputAdornment>
							<FieldValidationErrors errors={validationErrors} />
							<InputButton
								type="button"
								className={baseClass.element("button").toClassName()}
								onClick={decrement}
								disabled={readOnly || (!isNil(min) && value <= min)}
								tabIndex={-1}
								aria-controls={id}
							>
								<Icon name="mdi/minus" size={ICON_SIZE.XSMALL} />
							</InputButton>
							<InputButton
								type="button"
								className={baseClass.element("button").toClassName()}
								onClick={increment}
								disabled={readOnly || (!isNil(max) && value >= max)}
								tabIndex={-1}
								aria-controls={id}
							>
								<Icon name="mdi/plus" size={ICON_SIZE.XSMALL} />
							</InputButton>
						</InputAdornment>
					)
				}
			/>
		</FieldWrapper>
	);
};
